import {
  ChartPieSliceIcon,
  CoinsIcon,
  ExportIcon,
  FileTextIcon,
  FrameCornersIcon,
  HexagonIcon,
  HouseSimpleIcon,
  type Icon,
  PolygonIcon,
  PresentationChartIcon,
  SparkleIcon,
  SphereIcon,
  StrategyIcon,
  VaultIcon
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { ProjectNav as DsProjectNav } from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ProjectLayout/ui/blocks/ProjectNav";
import { SidebarProvider } from "@libs/zapi-ui/ds/components/sidebar";
import { useProjectNav } from "../hooks/useProjectNav";

const ENTRY_ICONS: Record<string, Icon> = {
  home: HouseSimpleIcon,
  exec: FileTextIcon,
  memo: FileTextIcon,
  risks: PolygonIcon,
  rqaSim: HexagonIcon,
  "section-market-analysis": ChartPieSliceIcon,
  "section-strategy-analysis": StrategyIcon,
  "section-financial-analysis": CoinsIcon,
  "section-appendices": SparkleIcon,
  "section-hidden": SparkleIcon,
  frame: FrameCornersIcon,
  modelLedger: SphereIcon,
  vault: VaultIcon,
  presentation: PresentationChartIcon,
  exports: ExportIcon
};

// Held open: the provider's ⌘B toggle would otherwise fire while bolding text in an editor.
const keepOpen = () => undefined;

export function ProjectNav({ projectId }: { projectId: string }) {
  const { t } = useTranslation("projectHome");
  const { groups, activeId, select } = useProjectNav(projectId);
  const entries = new Map(groups.flatMap((group) => group.entries.map((entry) => [entry.id, entry] as const)));

  return (
    <div data-testid="sidebar-chapters-root" className="contents">
      <SidebarProvider open onOpenChange={keepOpen} className="min-h-0 w-auto shrink-0">
        <DsProjectNav
          groups={groups.map((group) => ({
            id: group.id,
            label: group.label,
            items: group.entries.map(({ id, label, locked }) => ({
              id,
              label,
              locked,
              icon: ENTRY_ICONS[id] ?? FileTextIcon,
              href: `#${id}`
            }))
          }))}
          activeId={activeId}
          label={t("sidebar.navLabel")}
          lockedLabel={t("sidebar.locked")}
          lockedIconLabel={t("sidebar.lockedIcon")}
          renderLink={(item) => {
            const entry = entries.get(item.id);
            return (
              <button type="button" data-testid={entry?.testId} onClick={entry ? () => select(entry) : undefined} />
            );
          }}
        />
      </SidebarProvider>
    </div>
  );
}
